import { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";

import { alignItems, fileTypeConfigs, fileTypes, strCode } from "../../../consts";
import { t } from "../../../services";
import { fileUtils } from "../../../utils";

import WrapperContainer from "../../Containers/WrapperContainer";
import FlexContainers from "../../Containers/FlexContainers";
import { Paragraph2 } from "../../Text/Paragraphs";
import IconButton from "../../Buttons/IconButton";
import SVG from "../../SVG/icons";

import {
  ContentWrapper,
  FileInfoWrapper,
  FileName,
  ImagePreviewWrapper,
  UploadedFileWrapper
} from "../styled";

export const UploadedFile = ({
  file,
  defaultFileName,
  disabled,
  onRemoveFile = () => {}
}) => {
  const [imageSrc, setImageSrc] = useState();

  const isImage = useMemo(() => {
    const imageFileTypeConfig = fileTypeConfigs[fileTypes.image];

    return imageFileTypeConfig.extensions.includes(file?.type);
  }, [file]);

  const isEmpty = useMemo(() => {
    return !file?.size;
  }, [file]);

  const loadImage = useCallback(() => {
    if (isImage) {
      const src = URL.createObjectURL(file);
      setImageSrc(src);
      return () => {
        URL.revokeObjectURL(src);
      };
    } else {
      setImageSrc(null);
    }
  }, [file, isImage]);

  useEffect(() => {
    loadImage();
  }, [loadImage]);

  const handleRemoveFile = () => {
    setImageSrc(null);
    onRemoveFile();
  };
  return file ? (
    <UploadedFileWrapper Container={FlexContainers.FlexColumnStart} gap={4} width={"100%"}>
      <ContentWrapper
        Container={FlexContainers.FlexRowStart}
        alignItems={alignItems.start}
        width={"100%"}
        gap={8}
        hasImagePreview={!!imageSrc}>
        {imageSrc ? (
          <ImagePreviewWrapper>
            <img src={imageSrc} alt={"uploaded-image"} />
          </ImagePreviewWrapper>
        ) : (
          <SVG.UploadedFileIcon />
        )}

        <WrapperContainer Container={FlexContainers.FlexColumnStart} gap={4} width={"100%"}>
          <WrapperContainer
            Container={FlexContainers.FlexRowBetween}
            alignItems={alignItems.start}
            width={"100%"}
            gap={8}>
            <FileName>{file?.name ?? defaultFileName}</FileName>
            {!isEmpty && (
              <WrapperContainer mt={6} mr={6}>
                <IconButton
                  onClick={handleRemoveFile}
                  disabled={disabled}
                  tooltipLabel={t(strCode.REMOVE)}
                  xShift={-50}>
                  <SVG.DeleteIcon />
                </IconButton>
              </WrapperContainer>
            )}
          </WrapperContainer>

          <FileInfoWrapper Container={FlexContainers.FlexRowBetween} gap={4} width={"100%"}>
            <Paragraph2>
              {isEmpty
                ? t(strCode.UPLOADED)
                : t(strCode.UPLOADED_FILE_SIZE_INFO, {
                    size: fileUtils.transformFileSize(file?.size ?? 0)
                  })}
            </Paragraph2>
          </FileInfoWrapper>
        </WrapperContainer>
      </ContentWrapper>
    </UploadedFileWrapper>
  ) : null;
};

UploadedFile.propTypes = {
  file: PropTypes.object.isRequired,
  defaultFileName: PropTypes.string,
  deleteFileTooltip: PropTypes.string,
  disabled: PropTypes.bool,
  onRemoveFile: PropTypes.func.isRequired
};
